import { useInfiniteQuery } from '@tanstack/react-query';
import { SourcingService } from 'api-clients/monolith';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { ErrorState } from '@/components/ui/error-state';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { queryKeys } from '@/query-keys';

import { HiringGoalDetailsList } from './hiring-goal-details-list';
import { HiringGoalDetailsListSkeleton } from './hiring-goal-details-list-skeleton';
import { NoHiringGoals } from './no-hiring-goals';
import { GoalStatus } from './types';

interface HiringGoalsListContainerProps {
  activeTab: GoalStatus;
  onClose: () => void;
}

function HiringGoalsListContainer({
  activeTab,
  onClose,
}: HiringGoalsListContainerProps) {
  const openingSlug = useOpeningSlug();
  const intl = useIntl();
  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: [...queryKeys.openingDetails.hiringGoals, openingSlug, activeTab],
    queryFn: ({ pageParam }) =>
      SourcingService.getInternalApiSourcingOpeningsHiringGoals(
        openingSlug,
        pageParam,
        activeTab,
      ),
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage.pagination?.next_page ?? undefined,
  });

  const handleSetPage = () => {
    if (!isFetchingNextPage) void fetchNextPage({ cancelRefetch: false });
  };

  if (isLoading) {
    return <HiringGoalDetailsListSkeleton />;
  }

  const errorState = isError ? (
    <ErrorState
      message={intl.formatMessage({
        defaultMessage: "We couldn't load hiring goals. Try again.",
        description:
          'Hiring goals could not load; the recruiter can retry the request',
      })}
      onRetry={() =>
        void (isFetchNextPageError
          ? fetchNextPage({ cancelRefetch: false })
          : refetch())
      }
    />
  ) : null;

  if (isError && !data) return errorState;

  const hiringGoals = data?.pages.flatMap(page => page.hiring_goals) ?? [];
  if (hiringGoals.length === 0 && !isError) {
    return <NoHiringGoals activeTab={activeTab} />;
  }

  return (
    <>
      <HiringGoalDetailsList
        hiringGoals={hiringGoals}
        goalStatus={activeTab}
        pagination={data?.pages[data.pages.length - 1].pagination}
        setPage={handleSetPage}
        onClose={onClose}
      />
      {/* Inline skeleton while fetching more pages */}
      {isFetchingNextPage && <HiringGoalDetailsListSkeleton />}
      {errorState}
    </>
  );
}

export { HiringGoalsListContainer };
